import { useToast as useChakraToast, UseToastOptions } from '@chakra-ui/react'
import * as React from 'react'

import { Toast } from './Toast'

type THookOptions = {
  /**
   * ID unique for that toast message,
   * used to prevent toast duplication,
   * not needed for string messaged – in such case, the message is used as an ID
   */
  id?: string
  /**
   * time for which the toast should remain visible (in seconds)
   */
  durationS?: Nullable<number>
  /**
   * callback called when the toast has disappeared
   */
  onToastClosed?: UseToastOptions['onCloseComplete']
  /**
   * message/toast type or severity
   */
  type?: PropsOf<typeof Toast>['type']
  /**
   * custom icon to be rendered instead of the default one
   */
  customIcon?: PropsOf<typeof Toast>['customIcon']
}

type TCloseOptions =
  | {
      /**
       * if true, the toast can be closed by the user
       */
      isCloseable: true
      /**
       * callback called when the user clicks the close button
       */
      onCloseButtonClick?: () => void
    }
  | {
      isCloseable?: never
      onCloseButtonClick?: never
    }

type TToastOptions = THookOptions &
  TCloseOptions & {
    /**
     * the text message presented by the toast
     */
    message: PropsOf<typeof Toast>['message']
  }

const defaultMessageType = `info`
const defaultMessageDuration = 4 // 4s

/**
 * hook providing function for presenting toast (popups on top of the screen) with short messages
 * mainly use for presenting some quick information to the user
 * (like error messages or success confirmations)
 *
 * Returns a tuple:
 *  - function for presenting toast
 *  - function for closing toast (does not call `onToastClosed` callback)
 *
 * function for closing toast is useful when you want to close the toast differently
 * than by the customer clicking the close button or automatically after some time.
 *
 * arguments passed here are used as a defaults for all toasts made from the function this returns
 * and can be overridden by arguments passed to that function on each of its calls
 */
export const useToast = (hookOptions?: THookOptions) => {
  const presentChakraToast = useChakraToast()

  /**
   * function for presenting provided short text message in a form of a toast
   * (popup on top of the screen)
   * arguments passed here override the ones passed to the hook function
   */
  const presentToast = React.useCallback(
    ({
      id,
      type,
      message,
      durationS,
      customIcon,
      isCloseable,
      onToastClosed, // pfc
      onCloseButtonClick,
    }: TToastOptions) => {
      // get the string message if possible
      // used as an id if none is provided to prevent duplicate toasts
      const stringMessage = typeof message === 'string' ? message : undefined

      // value priority: function arguments > hook arguments > default values
      const toastId = id ?? hookOptions?.id ?? stringMessage
      const messageType = type ?? hookOptions?.type ?? defaultMessageType
      const onToastWasClosed = onToastClosed ?? hookOptions?.onToastClosed

      const messageDuration = durationS ?? hookOptions?.durationS ?? defaultMessageDuration

      // if `durationS` is null, the toast should not disappear automatically
      const shouldDisappear = durationS !== null
      const duration = shouldDisappear
        ? messageDuration * 1000 // transform s to ms
        : null

      // prevent showing duplicate toasts
      if (toastId && presentChakraToast.isActive(toastId)) {
        return
      }

      // close the toast and call the provided callback
      const closeToast = () => {
        if (!toastId || !isCloseable) return

        presentChakraToast.close(toastId)
        onCloseButtonClick?.()
      }

      presentChakraToast({
        duration,
        id: toastId,
        position: `top`,
        onCloseComplete: onToastWasClosed,
        render: () => (
          <Toast
            type={messageType}
            message={message}
            customIcon={customIcon}
            onClose={isCloseable ? closeToast : undefined}
          />
        ),
      })
    },
    [
      hookOptions?.id,
      hookOptions?.type,
      hookOptions?.durationS,
      hookOptions?.onToastClosed,
      presentChakraToast,
    ],
  )

  return [presentToast, (toastId: ID) => presentChakraToast.close(toastId)] as const
}
